import {
  type EditorOption,
  useMultiSelectTypeaheadEditor,
} from 'bundles/personalization-settings/hooks/useMultiSelectTypeaheadEditor';
import { addRole } from 'bundles/personalization-settings/utils/desiredRolesV1';
import type { SelectedRole } from 'bundles/personalization-settings/utils/desiredRolesV1';

/** A catalog role the parent makes available to pick from. */
export type AvailableRole = { id: string; name: string };

/** Sentinel key for the synthetic "Add '<input>'" option that creates a user-generated role. */
export const CREATE_USER_GENERATED_KEY = '__personalization_create_user_generated_role__';

export type RoleOption = EditorOption<SelectedRole>;

// Module-scope adapters — stable references so the options memo doesn't recompute unnecessarily.
const getItemId = (role: AvailableRole) => role.id;
const getItemName = (role: AvailableRole) => role.name;
const getSelectedCatalogId = (role: SelectedRole) => (role.type === 'DEFAULT' ? role.id : null);
const getSelectedName = (role: SelectedRole) => role.name;
const makeSelectedFromItem = (role: AvailableRole): SelectedRole => ({
  type: 'DEFAULT',
  id: role.id,
  name: role.name,
});
const makeUserGenerated = (name: string): SelectedRole => ({ type: 'CUSTOM', name });

type UseDesiredRolesEditorArgs = {
  value: SelectedRole[];
  onChange: (roles: SelectedRole[]) => void;
  availableRoles: readonly AvailableRole[];
  input: string;
};

export const useDesiredRolesEditor = ({ value, onChange, availableRoles, input }: UseDesiredRolesEditorArgs) => {
  const { options, trimmedInput, addFromOption, addFromInput } = useMultiSelectTypeaheadEditor({
    value,
    onChange,
    availableItems: availableRoles,
    input,
    getItemId,
    getItemName,
    getSelectedCatalogId,
    getSelectedName,
    makeSelectedFromItem,
    makeUserGenerated,
    addItem: addRole,
    createOptionKey: CREATE_USER_GENERATED_KEY,
  });

  return {
    options,
    trimmedInput,
    addRoleFromOption: addFromOption,
    addRoleFromInput: addFromInput,
  };
};

export default useDesiredRolesEditor;
